import Vue from 'vue';
import { Action } from '@/helpers/action';
import { ItemTextValuePairs } from '@/helpers/data_bank_items';
import { camelCase } from '@/helpers/string';

export class DataBank {
    public static dataBankSingleton: any = Vue.observable({});
    private static persistentData: Map<string, any> = new Map();
    private static persistentWatchers: Map<string, any[]> = new Map();
    private static resources: Array<string> = [];

    private static _watcherVm = new Vue({
        data: { state: DataBank.dataBankSingleton },
    });

    public constructor(params: any = null) {
        if (params == null) {
            return;
        }

        DataBank.createInstance(params);
    }

    static createInstance(params: any = null) {
        if (params == null) {
            return;
        }

        // the "reference" is an object that holds a reference to every field in the databank singleton while having a unique value for the context
        // note the prototype object creation ensures the reference also has access to data added to the databank after this object instance is created
        const reference = Object.create(DataBank.dataBankSingleton);
        reference.$ctx = params.ctx || null;

        // run before create if applicable
        if (params.beforeCreate && typeof params.beforeCreate === 'function') {
            params.beforeCreate.bind(DataBank.dataBankSingleton)();
        }

        // bind data to the data bank singleton
        if (params.data) {
            for (const [key, value] of Object.entries(params.data) as [string, any]) {

                // if data is already persistent, don't bind it to the data bank singleton
                if (DataBank.persistentData.has(key)) {
                    continue;
                }

                // if the object is persistent and doesn't already exists, bind it to the data bank singleton
                if (typeof value === 'object' && value !== null && value.persistent && value.expireTimer > 0) {
                    if (!value.currentExpireTimer) {
                        value.currentExpireTimer = value.expireTimer;
                    }

                    DataBank.persistentData.set(key, value);
                    
                    Object.defineProperty(DataBank.dataBankSingleton, key, {
                        get() {
                            // reset expire timer
                            value.currentExpireTimer = value.expireTimer;
                            return Vue.observable(value)?.value || null;
                        },
                        set(newVal) {
                            if (typeof newVal === 'object' && newVal?.persistent && newVal?.expireTimer > 0) {
                                value.value = newVal.value;
                            } else {
                                value.value = newVal;
                            }
                        },
                        configurable: true,
                        enumerable: true,
                    });
                } else {
                    // otherwise just set the data normally
                    Vue.set(DataBank.dataBankSingleton, key, value);
                }
            }
        }

        if (params.resources) {
            for (const resource of params.resources) {
                DataBank.initializeResource(resource, false);
            }
        }

        if (params.lazyResources) {
            for (const resource of params.lazyResources) {
                DataBank.initializeResource(resource, true);
            }
        }

        // bind computed
        if (params.computed) {
            for (const [key, definition] of Object.entries(params.computed) as [string, any]) {
        
                if (typeof definition === 'function') {
                    // Function shorthand (getter only)
                    Object.defineProperty(DataBank.dataBankSingleton, key, {
                        get() {
                            return definition.call(reference);
                        },
                        configurable: true,
                        enumerable: true,
                    });
                } 
                else if (definition && typeof definition === 'object' && typeof definition.get === 'function') {
                    // Full { get, set } syntax
                    Object.defineProperty(DataBank.dataBankSingleton, key, {
                        get() {
                            return definition.get.call(reference);
                        },
                        set: typeof definition.set === 'function'
                            ? function(val) {
                                return definition.set.call(reference, val);
                            }
                            : undefined,
                        configurable: true,
                        enumerable: true,
                    });
                }
            }
        }

        // bind methods to the data bank singleton
        if (params.methods) {
            for (const [key, value] of Object.entries(params.methods)) {
                // should always be true
                if (typeof value === 'function') {
                    Vue.set(DataBank.dataBankSingleton, key, value.bind(reference));
                } else {
                    Vue.set(reference, key, value);
                }
            }
        }

        // initialize watchers if applicable
        if (params.watch) {
            for (const [key, options] of Object.entries(params.watch) as [string, any][]) {
                if (!Object.prototype.hasOwnProperty.call(DataBank.dataBankSingleton, key) || !options || !options.handler) {
                    continue;
                }

                // check if the watcher is already defined (ie to make sure we don't add the same persistent watcher twice)
                if (options.persistent && DataBank.persistentWatchers.has(key) && DataBank.persistentWatchers.get(key)?.some((watcher: any) => watcher.handlerSrc === options.handler.toString())) {
                    continue;
                }
        
                // define an unwatch function to be called when the watcher falls out of scope
                const unwatch = DataBank._watcherVm.$watch(
                    () => DataBank.dataBankSingleton[key],
                    options.handler.bind(reference),
                    { deep: options.deep ?? false, immediate: options.immediate ?? false }
                );
        
                // if persistent, handle it through waiting until the watched item falls out of scope
                if (options.persistent) {
                    if (!DataBank.persistentWatchers.has(key)) {
                        DataBank.persistentWatchers.set(key, []);
                    }

                    DataBank.persistentWatchers.get(key)?.push({ handlerSrc: options.handler.toString(), watcherVm: unwatch });
                } else {
                    // otherwise, remove when the screen is unloaded
                    Action.Add(reference, 'OnNewSceneLoad', () => { unwatch(); });
                }
            }
        }

        // run created if applicable
        if (params.created && typeof params.created === 'function') {
            params.created.bind(reference)();
        }

        // run mounted if applicable
        if (params.mounted && typeof params.mounted === 'function') {
            if (params.ctx) {
                params.ctx.$nextTick(() => {
                    params.mounted.bind(reference)();
                });
            } else {
                params.mounted.bind(reference)();
            }
        }

        // setup destroyed if applicable
        if (params.destroyed && typeof params.destroyed === 'function') {
            Action.Add(reference, 'OnNewSceneLoad', params.destroyed.bind(reference));
        }
    }

    // returns the text for the given key
    static getItemText(key: string): string {
        const itemTextValuePair = ItemTextValuePairs.find((item) => item.key === key);
        return itemTextValuePair?.text || 'key';
    }
    
    // returns the value for the given key
    static getItemValue(key: string): string {
        const itemTextValuePair = ItemTextValuePairs.find((item) => item.key === key);
        return itemTextValuePair?.value || 'value';
    }

    // returns the divider variable for the given key
    static getDividerVariable(key: string): string {
        const itemTextValuePair = ItemTextValuePairs.find((item) => item.key === key);
        return itemTextValuePair?.dividerVariable || 'divider';
    }

    static async loadResource(resource: string): Promise<any> {
        const baseKey = `r_${camelCase(resource)}`;
        await DataBank.initializeResource(resource);
        return DataBank.dataBankSingleton[baseKey];
    }

    private static async initializeResource(resource: string, isLazy: boolean = false) {
        const itemTextValuePair = ItemTextValuePairs.find((item) => item.key === resource);
        if (!itemTextValuePair) {
            return;
        }
    
        const baseKey = `r_${camelCase(resource)}`;
    
        // If already initialized and persistent, refresh its timer
        if (DataBank.persistentData.has(baseKey)) {
            const persistentObj = DataBank.persistentData.get(baseKey);
            persistentObj.currentExpireTimer = persistentObj.expireTimer;
            return;
        }
    
        // Make the inner "value" object observable so Vue can track changes to its fields
        const innerValue = Vue.observable({
            [`${baseKey}`]: null,
            [`${baseKey}IsLoading`]: false,
            [`${baseKey}HasLoaded`]: false,
            [`${baseKey}Error`]: null,
            [`${baseKey}HasError`]: false,
        });
    
        const persistentObj = {
            persistent: true,
            expireTimer: itemTextValuePair.expireTimes || 1,
            currentExpireTimer: itemTextValuePair.expireTimes || 1,
            value: innerValue,

            // load uses Vue.set to update properties so reactivity is guaranteed
            async load() {
                const val = this.value;
    
                // NOTE: use Vue.set to be safe when mutating from async context
                Vue.set(val, `${baseKey}IsLoading`, true);
                Vue.set(val, `${baseKey}HasLoaded`, false);
                Vue.set(val, `${baseKey}HasError`, false);
                Vue.set(val, `${baseKey}Error`, null);
    
                try {
                    const result = await itemTextValuePair.getFunc();
                    if (result && !result.error) {
                        Vue.set(val, `${baseKey}`, result.value);
                    } else {
                        Vue.set(val, `${baseKey}Error`, result?.error ?? 'Unknown error');
                        Vue.set(val, `${baseKey}HasError`, true);
                    }
                } catch (err: any) {
                    Vue.set(val, `${baseKey}Error`, err?.message ?? 'Exception thrown');
                    Vue.set(val, `${baseKey}HasError`, true);
                } finally {
                    Vue.set(val, `${baseKey}IsLoading`, false);
                    Vue.set(val, `${baseKey}HasLoaded`, true);
                }
            },
        };
    
        // store persistent object
        DataBank.persistentData.set(baseKey, persistentObj);
    
        // keep resources list in sync
        if (!Array.isArray(DataBank.resources)) {
            DataBank.resources = [];
        }
        DataBank.resources.push(baseKey);
    
        // adds the fields to the singleton
        Object.keys(innerValue).forEach((innerKey) => {
            // Avoid redefining if property already exists
            if (Object.prototype.hasOwnProperty.call(DataBank.dataBankSingleton, innerKey)) {
                return;
            }
    
            Object.defineProperty(DataBank.dataBankSingleton, innerKey, {
                get() {
                    if (!persistentObj.value[`${baseKey}HasLoaded`] && !persistentObj.value[`${baseKey}IsLoading`]) {
                        persistentObj.load();
                    }

                    // refresh timer whenever any of the fields are accessed
                    persistentObj.currentExpireTimer = persistentObj.expireTimer;                    
                    return persistentObj.value[innerKey];
                },
                set(newVal) {
                    // use Vue.set to ensure reactivity when values are changed
                    Vue.set(persistentObj.value, innerKey, newVal);
                },
                configurable: true,
                enumerable: true,
            });
        });
    
        if (!isLazy) {
            // initial load
            await persistentObj.load();
        }
    }

    static onViewLoad() {
        // run action
        Action.Emit('OnNewSceneLoad');
        Action.Clear('OnNewSceneLoad');

        // cleanup data
        Object.keys(DataBank.dataBankSingleton).forEach((key) => {
            if (key.startsWith('r_')) {
                return;
            }

            // if the item is persistent, lower the timer and delete if expired
            if (DataBank.persistentData.has(key)) {
                const persistentData = DataBank.persistentData.get(key);

                // lower timer and delete if expired
                persistentData.currentExpireTimer--;
                if (persistentData.currentExpireTimer <= 0) {
                    Vue.delete(DataBank.dataBankSingleton, key);
                    DataBank.persistentData.delete(key);
                }
            } else {
                // if the item is not persistent, delete it
                Vue.delete(DataBank.dataBankSingleton, key);
            }
        });

        for (const resource of DataBank.resources) {
            const persistentData = DataBank.persistentData.get(resource);
        
            if (!persistentData) {
                continue;
            }
        
            // Decrement the resource’s timer
            persistentData.currentExpireTimer--;
        
            // If expired, clean up everything
            if (persistentData.currentExpireTimer <= 0) {
                // Delete all reactive fields associated with this resource
                for (const innerKey of Object.keys(persistentData.value)) {
                    Vue.delete(DataBank.dataBankSingleton, innerKey);
                }
        
                // Remove from persistentData and active resources
                DataBank.persistentData.delete(resource);
                DataBank.resources = DataBank.resources.filter((r) => r !== resource);
            }
        }

        for (const [key, watchers] of DataBank.persistentWatchers) {
            if (Object.prototype.hasOwnProperty.call(DataBank.dataBankSingleton, key)) {
                continue;
            }

            for (const watcher of watchers) {
                watcher.watcherVm.$destroy();
            }

            DataBank.persistentWatchers.delete(key);
        }
    }
}

export const dataBank = DataBank.dataBankSingleton;
